import {
  Flex,
  Button as MantineButton,
} from '@mantine/core';
import { useForm } from "@mantine/form";
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { notifications, showNotification } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { ReactComponent as IconWarning } from "src/assets/icons/toast-warning.svg";
import {
  validateGuardianAddress, validateGuardianEmail, validateGuardianName, validateGuardianPhone, validateGuardianRelation,
} from 'src/features/nomineeDetailsForm/nomineeValidate';
import dayjs from 'dayjs';
import {
  optArr, titleOptionsArr,
} from 'src/constants/nomineeForm';
import useCleverTapEvents from 'src/events/useGetEvents';
import { NOMINEE_EVENT } from 'src/events/constant';
import NomineeDetailsForm from "./nomineeDetailsForm";
import { ScreenWrapper } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { RootState } from '../../redux/store';
import { useStyles } from './nomineeDetails.styles';
import {
  clearNomineeDetailsFormError,
  fetchPincodeInfo,
  saveNomineeDetails,
  setNomineeDetailsFormStatus,
  skipNomineeDetails,
} from './nomineeDetailsFormSlice';
import { getAge, getProgressStepper } from '../../utils/helpers';
import { ScreenLoader } from '../../components/screenLoader';
import { Button } from '../../components/atoms';

const UserInitialState = {
  title: '',
  firstName: '',
  lastName: '',
  relationship: '',
  dateOfBirth: '',
  guardianName: '',
  guardianAddress: '',
  guardianEmail: '',
  guardianPhone: '',
  guardianRelation: '',
  phone: '',
  email: '',
  addressLineOne: '',
  addressLineTwo: '',
  pincode: '',
  percentage: '',
  id: Math.random().toString(), // Math.random is used coz we don't have more than 3 nominees
}

export const NomineeDetails = () => {
  const { classes } = useStyles();
  const { TriggerEvent } = useCleverTapEvents()
  const nomineeDetailsState = useSelector((state: RootState) => state.nomineeDetails);
  const { pincodeDetails, isPincodeValid } = nomineeDetailsState;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentStep = useSelector((state: RootState) => state.account.userDetails.currentStep);

  const form = useForm({
    initialValues: {
      users: [UserInitialState],
    },
    validate: {
      users: {
        title: (value) => (!titleOptionsArr.includes(value) ? 'Please select title' : null),
        firstName: (value) => (value.length <= 0 ? "Name cannot be empty" : null),
        lastName: (value) => (value.length <= 0 ? "Name cannot be empty" : null),
        relationship: (value) => (!optArr.includes(value) ? 'Please select relationship' : null),
        email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
        phone: (value) => (/^(\d{10})$/.test(value) ? null : 'Invalid mobile number'),
        pincode: (value) => (/^(\d{4}|\d{6})$/.test(value) ? null : 'Invalid pincode'),
        dateOfBirth: (value) => (value && getAge(value) >= 0 ? null : 'Nominee Date of birth is required'),
        addressLineOne: (value) => (value.length < 8 ? 'Address is too short' : null),
        guardianName: (value, values, currentPath) => validateGuardianName(value, values, currentPath),
        guardianAddress: (value, values, currentPath) => validateGuardianAddress(value, values, currentPath),
        guardianPhone: (value, values, currentPath) => validateGuardianPhone(value, values, currentPath),
        guardianEmail: (value, values, currentPath) => validateGuardianEmail(value, values, currentPath),
        guardianRelation: (value, values, currentPath) => validateGuardianRelation(value, values, currentPath),
      },
    },
  });

  useEffect(() => {
    const nomineeOnepincodeString = form.values?.users[0]?.pincode.toString() || '';
    if (nomineeOnepincodeString.length === 6 && !pincodeDetails[nomineeOnepincodeString]) {
      dispatch(fetchPincodeInfo({ pincode: nomineeOnepincodeString }));
    }
  }, [form.values.users[0]?.pincode]);

  useEffect(() => {
    const nomineeTwopincodeString = form.values?.users[1]?.pincode.toString() || '';
    if (nomineeTwopincodeString.length === 6 && !pincodeDetails[nomineeTwopincodeString]) {
      dispatch(fetchPincodeInfo({ pincode: nomineeTwopincodeString }));
    }
  }, [form.values.users[1]?.pincode]);

  useEffect(() => {
    const nomineeThreepincodeString = form.values?.users[2]?.pincode.toString() || '';
    if (nomineeThreepincodeString.length === 6 && !pincodeDetails[nomineeThreepincodeString]) {
      dispatch(fetchPincodeInfo({ pincode: nomineeThreepincodeString }));
    }
  }, [form.values.users[2]?.pincode]);

  useEffect(() => {
    if (nomineeDetailsState.status === 'success') {
      dispatch(setNomineeDetailsFormStatus('idle'));
      if (nomineeDetailsState.nomineeDetailsSkipped) {
        notifications.show({
          color: 'orange',
          message: 'Nominee Details Skipped!',
          icon: <IconWarning />,

        });
      } else {
        notifications.show({
          color: 'green',
          message: 'Nominee Details Saved!',
          icon: <IconSuccess />,
        });
      }
      navigate('/add-income-details');
    } else if (nomineeDetailsState.status === 'error') {
      dispatch(clearNomineeDetailsFormError());
      notifications.show({
        color: 'red',
        message: nomineeDetailsState.error.message || 'Could\'nt save nominee details, Try again!',
        icon: <IconError />,
      });
    }
  }, [nomineeDetailsState.status]);

  const onSkip = () => {
    dispatch(skipNomineeDetails({}));
    TriggerEvent(NOMINEE_EVENT.nominee, { option: "Don't want to" })
  };

  const handleFormSubmit = () => {
    const log = form.validate();
    if (log.hasErrors) {
      showNotification({
        title: 'Invalid Details',
        message: 'Please fill all the details correctly',
        color: 'red',
        icon: <IconError />,
      });
    }

    if (!log.hasErrors) {
      const isAllValidPin = form.values.users.every((user: any, index) => {
        const validPin = isPincodeValid[user.pincode];
        if (!validPin) {
          form.setFieldError(`users.${index}.pincode`, 'Pincode is invalid');
          showNotification({
            title: `Invalid Nominee ${index + 1} Pincode Details`,
            message: 'Make sure all the pincode are valid',
            color: 'red',
            icon: <IconError />,
          });
        }
        return validPin;
      });

      if (!isAllValidPin) {
        return;
      }

      const percentageSum = form.values.users.reduce((acc, curr) => acc + Number(curr.percentage), 0);

      if (percentageSum !== 100) {
        showNotification({
          message: 'Total percentage of all nominees must be equal to 100',
          color: 'red',
          icon: <IconError />,
        });
        return;
      }

      const payload = form.values.users.map((user: any) => {
        const pinDetails = pincodeDetails[user.pincode];
        return {
          title: user.title,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone.toString(),
          email: user.email,
          relation: user.relationship,
          line1: user.addressLineOne,
          line2: user.addressLineTwo,
          pincode: user.pincode.toString(),
          city: pinDetails.district,
          state: pinDetails.state,
          country: pinDetails.country,
          sharePercentage: user.percentage,
          dateOfBirth: dayjs(user.dateOfBirth).format("DD/MM/YYYY"),
          guardianName: user.guardianName,
          guardianAddress: user.guardianAddress,
          guardianPhone: user.guardianPhone.toString(),
          guardianEmail: user.guardianEmail,
          guardianRelation: user.guardianRelation,
        };
      })

      dispatch(saveNomineeDetails(payload));
      TriggerEvent(NOMINEE_EVENT.nominee, { option: "Saved" })
    }
  };

  const renderFooter = () => (
    <Flex pos="absolute" w="100%" bottom={0} className={classes.footerContainer}>
      <div className="csFooter">
        <div className={classes.footerButtonWrapper}>
          <MantineButton
            classNames={{
              label: classes.skipBtnLabel,
            }}
            radius="md"
            variant="outline"
            onClick={onSkip}
          >
            I do not wish to nominate
          </MantineButton>
          <Button type="submit" onClick={handleFormSubmit}>
            Save
          </Button>
        </div>
      </div>
    </Flex>
  );

  const handleAddUser = () => {
    if (form.values.users.length < 3) {
      form.setFieldValue("users", [...form.values.users, UserInitialState]);
    }
  };

  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(currentStep)} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">
            Add nominee details
          </h2>
          <NomineeDetailsForm form={form} />
          <button
            type="button"
            className={classes.changeNumberLink}
            onClick={handleAddUser}
            disabled={form.values.users.length >= 3}
          >
            + Add another nominee details
          </button>
        </div>
        {renderFooter()}
      </div>
      {nomineeDetailsState.status === 'loading' ? (<ScreenLoader />) : null}
    </ScreenWrapper>
  );
};

export default NomineeDetails;
